import { useEffect, useState } from "react";
import { Col, Form, Input, InputNumber, Row } from "antd";
import { editSettings } from "../../Network";
import { toastError, toastSuccess } from "../../helpers/toasters";
import { handleAllowNumbersOnly } from "helpers/helpfulFunctions";
import PrimaryButton from "modules/SubComponents/Buttons/PrimaryButton";

const KNOWN_SETTINGS = {
  1: { type: "whatsapp" }, // WhatsAppNumber
  2: { type: "percent" }, // GreenMatterPercentageForStoring
  3: { type: "number" }, // Add balance to buy and supply supply wallet period
  6: { type: "number" }, // Available Purchase Limit
  7: { type: "number" }, // Monthly Purchase Limit
  8: { type: "number" }, // Monthly Purchase Limit For Sales (FullTime)
  9: { type: "number" }, // Monthly Purchase Limit For Sales (PartTime)
  11: { type: "nonNegativeNumber" }, // B&S Available Purchase Limit
  12: { type: "nonNegativeNumber" }, // B&S Monthly Purchase Limit
  13: { type: "nonNegativeNumber" }, // B&S Monthly Purchase Limit For Sales (FullTime)
  14: { type: "nonNegativeNumber" }, // B&S Monthly Purchase Limit For Sales (PartTime)
};

export const humanizeKey = (key = "") => {
  if (/\s/.test(key)) return key;
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
};

const getSettingType = (item) => {
  if (KNOWN_SETTINGS[item.stringId]) {
    return KNOWN_SETTINGS[item.stringId].type;
  }

  const key = (item.key || "").toLowerCase();
  const value = String(item.value ?? "");

  if (
    key.includes("phone") ||
    key.includes("whatsapp") ||
    key.includes("number") ||
    value.startsWith("+")
  ) {
    return "phone";
  }
  if (key.includes("percent")) {
    return "percent";
  }
  if (
    key.includes("limit") ||
    key.includes("period") ||
    (value !== "" && !Number.isNaN(Number(value)))
  ) {
    return "number";
  }
  return "text";
};

const validatePhoneNumber = (phone) => {
  let phoneLen = 11;
  let charLen = 2;
  if (phone.startsWith("2")) {
    phoneLen = 12;
    charLen = 3;
  }
  if (
    phone.length > phoneLen ||
    (phone.length >= charLen &&
      !phone.startsWith(`${charLen === 2 ? "0" : "20"}1`)) ||
    (phone.length >= charLen + 1 &&
      !phone.startsWith(`${charLen === 2 ? "0" : "20"}10`) &&
      !phone.startsWith(`${charLen === 2 ? "0" : "20"}11`) &&
      !phone.startsWith(`${charLen === 2 ? "0" : "20"}12`) &&
      !phone.startsWith(`${charLen === 2 ? "0" : "20"}15`)) ||
    phone.length < phoneLen
  ) {
    return false;
  }
  return true;
};

const validateWhatsApp = (_, value) => {
  if (!value) {
    return Promise.reject(new Error("Please, Enter the whats app number"));
  }
  if (!validatePhoneNumber(value)) {
    return Promise.reject(new Error("Please, Enter a valid whats app number"));
  }
  return Promise.resolve();
};

const validatePercent = (_, value) => {
  if (!value && value !== 0) {
    return Promise.reject(new Error("Please, Enter the commission"));
  }
  if (value <= 0 || value > 100) {
    return Promise.reject(new Error("Please, Enter a valid commission"));
  }
  return Promise.resolve();
};

const validatePositiveNumber = (label) => (_, value) => {
  if (!value && value !== 0) {
    return Promise.reject(
      new Error(`Please, Enter the ${label.toLowerCase()}`),
    );
  }
  if (value <= 0) {
    return Promise.reject(
      new Error(`Please, Enter a valid ${label.toLowerCase()}`),
    );
  }
  return Promise.resolve();
};

const validateNonNegativeNumber = (label) => (_, value) => {
  if (!value && value !== 0) {
    return Promise.reject(
      new Error(`Please, Enter the ${label.toLowerCase()}`),
    );
  }
  if (value < 0) {
    return Promise.reject(
      new Error(`Please, Enter a valid ${label.toLowerCase()}`),
    );
  }
  return Promise.resolve();
};

const validatePhone = (label) => (_, value) => {
  if (!value) {
    return Promise.reject(
      new Error(`Please, Enter the ${label.toLowerCase()}`),
    );
  }
  if (!/^\+?\d{8,15}$/.test(value)) {
    return Promise.reject(
      new Error(`Please, Enter a valid ${label.toLowerCase()}`),
    );
  }
  return Promise.resolve();
};

const validateRequired = (label) => (_, value) => {
  if (value === undefined || value === null || value === "") {
    return Promise.reject(
      new Error(`Please, Enter the ${label.toLowerCase()}`),
    );
  }
  return Promise.resolve();
};

const handlePhoneKeyDown = (e) => {
  const isCopyingOrPasting = e.ctrlKey && /^[cva]$/i.test(e.key);
  if (
    e.key === "Backspace" ||
    e.key === "+" ||
    e.key.startsWith("ArrowLeft") ||
    e.key.startsWith("ArrowRight") ||
    isCopyingOrPasting
  ) {
    return;
  }
  if (/\D+/gi.test(e.key)) {
    e.preventDefault();
  }
};

const getValidator = (type, label) => {
  if (type === "whatsapp") return validateWhatsApp;
  if (type === "percent") return validatePercent;
  if (type === "number") return validatePositiveNumber(label);
  if (type === "nonNegativeNumber") return validateNonNegativeNumber(label);
  if (type === "phone") return validatePhone(label);
  return validateRequired(label);
};

const SettingItem = ({ item }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const type = getSettingType(item);
  const label = humanizeKey(item.key);
  const isNumberInput =
    type === "percent" || type === "number" || type === "nonNegativeNumber";

  useEffect(() => {
    const raw = item.value ?? "";
    const value =
      isNumberInput && raw !== "" && !Number.isNaN(Number(raw))
        ? Number(raw)
        : raw;
    form.setFieldsValue({ value });
  }, [item, form, isNumberInput]);

  const handleFinish = (values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    editSettings(
      {
        id: item.id,
        value: String(values.value),
      },
      (res) => {
        setIsSubmitting(false);
        if (res.success) {
          toastSuccess(res.message);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        setIsSubmitting(false);
        toastError(res?.message || res?.data?.error);
      },
    );
  };

  return (
    <section className="mb-5">
      <Form
        form={form}
        className="px-lg-5 px-2"
        onFinish={handleFinish}
        initialValues={{ value: "" }}
        disabled={item.stringId === "4" || item.stringId === "5"}
      >
        <Row gutter={24}>
          <Col span={24}>
            <div className="new-actor__name new-category mb-5">
              <div className="lang-name wd-100 flex ">
                <label className="users__parent-info-label mb-2" htmlFor="">
                  {label}
                </label>
              </div>
              <Form.Item
                className="new-category__form-item"
                name="value"
                rules={[{ validator: getValidator(type, label) }]}
              >
                {isNumberInput ? (
                  <InputNumber
                    className="w-100 d-block search__searchField py-2"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    onKeyDown={(e) =>
                      handleAllowNumbersOnly(e, type === "percent")
                    }
                    controls={false}
                    keyboard={false}
                  />
                ) : (
                  <Input
                    className="w-100 d-block search__searchField py-2"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    onKeyDown={
                      type === "whatsapp"
                        ? (e) => handleAllowNumbersOnly(e, false)
                        : type === "phone"
                          ? handlePhoneKeyDown
                          : undefined
                    }
                  />
                )}
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <PrimaryButton
              buttonName="Submit"
              loading={isSubmitting}
              type="submit"
            />
          </Col>
        </Row>
      </Form>
    </section>
  );
};

export default SettingItem;
